import React, { useCallback, useEffect, useState } from "react";
import { ArrowRight, Check, Loader2, RotateCcw, Wand2 } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { api, errMsg } from "@/lib/api";
import { Inspector } from "@/components/canvas/Inspector";
import { K } from "@/components/canvas/kalkyle/SummarySections";
import { ago } from "@/lib/sk";

const KIND = {
  role: ["rad er informasjon, ikke arbeid", "rader er informasjon, ikke arbeid"],
  qty: ["mengde uten tall", "mengder uten tall"],
  price_type: ["post uten prisform", "poster uten prisform"],
  clarification: ["åpen avklaring", "åpne avklaringer"],
  basis: ["post med endret grunnlag", "poster med endret grunnlag"],
  status: ["post ikke merket ferdig", "poster ikke merket ferdig"],
};
const KIND_TITLE = { role: "Radtype", qty: "Mengde", price_type: "Prisform", clarification: "Avklaringer", basis: "Grunnlag", status: "Merkes ferdig" };
const solid = "inline-flex h-9 items-center justify-center gap-1.5 rounded-full bg-[hsl(var(--navy))] px-4 text-[13.5px] font-semibold text-white transition-colors hover:bg-[hsl(var(--ink-2))] disabled:opacity-40";
const quiet = "inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-[13.5px] font-medium text-foreground/70 transition-colors hover:bg-black/[0.05] hover:text-foreground disabled:opacity-40";

/** Hurtigfullfør: what one press would assume (read-only until «Legg inn»), what still needs a person, and what was assumed earlier. */
export function useHurtigfullfor(caseId, key) {
  const [data, setData] = useState(null);
  const [working, setWorking] = useState(null);
  const load = useCallback(async () => {
    try { const r = await api.get(`/projects/${caseId}/kalkyle/hurtigfullfor`); setData(r.data); } catch { /* the card simply stays quiet */ }
  }, [caseId]);
  useEffect(() => { load(); }, [load, key]);
  const run = useCallback(async (name, fn) => {
    setWorking(name);
    try { return await fn(); } catch (e) { toast.error(errMsg(e)); return null; } finally { setWorking(null); }
  }, []);
  const apply = useCallback((ops) => run("apply", async () => {
    const r = await api.post(`/projects/${caseId}/kalkyle/hurtigfullfor`);
    ops.apply?.({ context: r.data.context });
    await load();
    return r.data;
  }), [caseId, run, load]);
  const undo = useCallback((changeId, ops) => run("undo", async () => { await ops.undo(changeId); await load(); return true; }), [run, load]);
  const confirm = useCallback((body, ops) => run(`confirm-${body.item_id || body.element_id}`, async () => {
    const r = await api.post(`/projects/${caseId}/kalkyle/hurtigfullfor/bekreft`, body);
    ops.apply?.({ context: r.data.context });
    setData((d) => (d ? { ...d, assumptions: r.data.assumptions } : d));
    return true;
  }), [caseId, run]);
  return { data, working, reload: load, apply, undo, confirm };
}

export const summarize = (plan) => Object.entries(plan?.by_kind || {}).map(([k, n]) => `${n} ${KIND[k]?.[n === 1 ? 0 : 1] || k}`);

/**
 * «Gjør tilbudsklar» – the card in Økonomi that says, in one breath, exactly what keeps the sum from being complete and offers
 * the one-press way through. Shown while the calculation is incomplete, and again while assumptions wait to be reviewed.
 */
export function KlargjorCard({ hf, ov, assumed, onOpen }) {
  const d = hf.data;
  if (!d) return null;
  const n = d.count || 0;
  const rem = d.remaining?.length || 0;
  const ass = d.assumptions?.length || 0;
  const incomplete = ov.stage === "estimert" || ov.stage === "start";
  if (!incomplete && !ass) return null;
  if ((ov.stage === "start" || assumed) && !n && !rem && !ass) return null; // nothing to fill in: the step is the KI-utkast (or confirming it), not price forms
  const parts = summarize(d);
  return (
    <section className="mt-8 rounded-[16px] bg-[hsl(var(--navy)/0.045)] px-5 py-4" data-testid="kalkyle-klargjor" data-steps={n} data-remaining={rem} data-assumptions={ass}>
      {incomplete ? (
        <>
          <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
            <div className="min-w-0">
              <div className="text-[15px] font-semibold text-foreground" data-testid="kalkyle-klargjor-title">{n || rem ? "Det som stopper tilbudssummen" : "Summen venter på kalkylen"}</div>
              <p className="k-meta mt-1" data-testid="kalkyle-klargjor-sub">
                {parts.length ? parts.join(" · ") : null}
                {rem ? <span className={cn(parts.length && "before:content-['_·_']")}><span className="text-[hsl(var(--v-assumed))]">{rem} {rem === 1 ? "linje uten pris" : "linjer uten pris"} – må settes av deg</span></span> : null}
                {!parts.length && !rem ? "Legg inn kostlinjer på postene, eller be assistenten foreslå kalkyle." : null}
              </p>
            </div>
            {n || rem ? <button type="button" onClick={onOpen} className={cn(solid, "shrink-0 self-start")} data-testid="kalkyle-klargjor-open"><Wand2 className="h-3.5 w-3.5" /> Fyll inn det som mangler</button> : null}
          </div>
          {n ? <p className="k-meta mt-2">Alt som fylles inn merkes «antatt», står på posten med navn og tid, og kan angres i ett trykk.</p> : null}
        </>
      ) : null}
      {ass ? (
        <div className={cn("flex flex-col items-start gap-2.5 sm:flex-row sm:items-center sm:justify-between sm:gap-4", incomplete && "mt-3 border-t border-black/[0.06] pt-3")}>
          <div className="min-w-0">
            <div className="text-[15px] font-medium text-foreground" data-testid="kalkyle-klargjor-assumptions">{ass} {ass === 1 ? "antakelse å gå gjennom" : "antakelser å gå gjennom"} før tilbudet sendes</div>
            <p className="k-meta mt-0.5">Hver rad er merket «antatt» – bekreft dem én for én, eller angre alt.</p>
          </div>
          <button type="button" onClick={onOpen} className={cn(quiet, "shrink-0")} data-testid="kalkyle-klargjor-review">Gå gjennom <ArrowRight className="h-3.5 w-3.5" /></button>
        </div>
      ) : null}
    </section>
  );
}

function Sec({ title, aside, children, testId }) {
  return (
    <section className="mt-7 first:mt-0" data-testid={testId}>
      <div className="flex items-baseline justify-between gap-3"><h3 className="k-label">{title}</h3>{aside}</div>
      <div className="mt-2">{children}</div>
    </section>
  );
}

/** One line in «Dette må du sette selv»: the row, and a price field that saves on Enter/blur – no detour through the post. */
function PriceLine({ r, ops, onOpen }) {
  const [v, setV] = useState("");
  const [saving, setSaving] = useState(false);
  const save = async () => {
    const n = parseFloat(String(v).replace(/\s/g, "").replace(",", "."));
    if (!v || Number.isNaN(n)) return;
    setSaving(true);
    try { await ops.patchElement(r.element_id, "unit_price", n); toast.success(`${r.ref}: pris satt til ${K(n)}${r.unit ? ` per ${r.unit}` : ""}`); setV(""); } catch { /* vist */ } finally { setSaving(false); }
  };
  return (
    <li className="flex flex-wrap items-center gap-x-3 gap-y-2 border-t border-black/[0.06] py-3 first:border-t-0" data-testid={`klargjor-remaining-${r.ref}-${r.element_id || "post"}`}>
      <button type="button" onClick={() => onOpen(r.item_id)} className="min-w-0 flex-1 text-left" data-testid={`klargjor-remaining-open-${r.ref}`}>
        <span className="block text-[14.5px] leading-[1.4] text-foreground"><span className="font-mono text-[13px]">{r.ref}</span> · {r.line || r.title}</span>
        <span className="k-meta block">{r.kind === "mengde" ? "enhetspris uten anslått mengde – åpne posten og sett mengden" : [r.element_kind, r.qty != null ? `${r.qty} ${r.unit || ""}`.trim() : null, r.unit_cost != null ? `kost ${K(r.unit_cost)}` : "kost ukjent"].filter(Boolean).join(" · ")}</span>
      </button>
      {r.element_id ? (
        <label className="flex h-9 items-center gap-1.5 rounded-[10px] border border-black/[0.1] bg-white px-2.5 text-[13.5px] focus-within:border-black/[0.3]">
          <input value={v} onChange={(e) => setV(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") save(); }} onBlur={() => v && save()} inputMode="decimal" placeholder="Pris" className="w-[84px] bg-transparent text-right tabular-nums outline-none placeholder:text-muted-foreground/60" aria-label={`Pris for ${r.ref}`} data-testid={`klargjor-price-${r.element_id}`} />
          <span className="k-meta">kr{r.unit ? `/${r.unit}` : ""}</span>
          {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" /> : null}
        </label>
      ) : null}
    </li>
  );
}

/**
 * The Hurtigfullfør panel beside the calculation. Three honest parts: what one press assumes (listed, not hidden), what no
 * assumption can answer (a price nobody has given – set it here), and the assumptions already made, to confirm or undo.
 */
export function KlargjorPanel({ hf, ops, onClose, onOpenItem }) {
  const d = hf.data;
  const [groups, setGroups] = useState({});
  if (!d) return <Inspector onClose={onClose} eyebrow="Hurtigfullfør" title="Leser kalkylen …" testId="klargjor-panel" label="Hurtigfullfør" noWide><div className="flex h-24 items-center justify-center"><Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /></div></Inspector>;
  const steps = d.steps || [];
  const rem = d.remaining || [];
  const ass = d.assumptions || [];
  const byKind = Object.keys(KIND_TITLE).map((k) => [k, steps.filter((s) => s.kind === k)]).filter(([, l]) => l.length);
  const n = steps.length;
  const title = n ? `${n} ${n === 1 ? "antakelse" : "antakelser"} fyller inn det som mangler` : rem.length ? "Bare priser gjenstår – og de må en person sette" : ass.length ? `${ass.length} ${ass.length === 1 ? "antakelse" : "antakelser"} å gå gjennom` : "Ingenting mangler";
  const sub = n ? `På ${d.items} ${d.items === 1 ? "post" : "poster"}. Ingenting legges inn før du trykker «Legg inn».` : rem.length ? "Ingen antakelse kan finne på en pris." : ass.length ? "Bekreft radene én for én, eller angre alt." : "Kalkylen har alt den trenger for en komplett sum.";
  const applyAll = async () => {
    const r = await hf.apply(ops);
    if (!r) return;
    const left = r.remaining?.length || 0;
    toast.success(`${r.count} ${r.count === 1 ? "antakelse" : "antakelser"} lagt inn${left ? ` – ${left} ${left === 1 ? "pris" : "priser"} må fortsatt settes` : " – summen er komplett"}`, r.context?.undo ? { action: { label: "Angre", onClick: () => hf.undo(r.context.undo.id, ops) } } : undefined);
  };
  const footer = n ? (
    <div className="flex items-center gap-2 border-t border-black/[0.06] bg-white px-6 py-4" data-testid="klargjor-footer">
      <button type="button" onClick={applyAll} disabled={Boolean(hf.working)} className={solid} data-testid="klargjor-apply">{hf.working === "apply" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-3.5 w-3.5" />} Legg inn {n} {n === 1 ? "antakelse" : "antakelser"}</button>
      <button type="button" onClick={onClose} className={quiet} data-testid="klargjor-cancel">Avbryt</button>
    </div>
  ) : null;
  return (
    <Inspector onClose={onClose} eyebrow="Hurtigfullfør" title={title} sub={sub} testId="klargjor-panel" label="Hurtigfullfør" noWide footer={footer}>
      {n ? (
        <Sec title="Dette fylles inn" aside={<span className="k-meta">{n}</span>} testId="klargjor-steps">
          {byKind.map(([k, list]) => {
            const open = groups[k] ?? list.length <= 6;
            return (
              <div key={k} className="border-t border-black/[0.06] py-2.5 first:border-t-0" data-testid={`klargjor-kind-${k}`}>
                <button type="button" onClick={() => setGroups((g) => ({ ...g, [k]: !open }))} className="flex w-full items-baseline justify-between gap-3 text-left" aria-expanded={open}>
                  <span className="text-[14.5px] font-medium text-foreground">{KIND_TITLE[k]} <span className="k-meta">· {list.length} {KIND[k][list.length === 1 ? 0 : 1]}</span></span>
                  <span className="k-meta">{open ? "Skjul" : "Vis"}</span>
                </button>
                {open ? <ul className="mt-1.5 space-y-1">{list.map((s, i) => <li key={`${s.item_id}-${s.element_id || s.clar_id || i}`} className="flex gap-2 text-[13.5px] leading-[1.45] text-foreground/85" data-testid={`klargjor-step-${k}-${s.ref}`}><span className="mt-[7px] h-1.5 w-1.5 shrink-0 rounded-full bg-[hsl(var(--v-assumed))]" />{s.text}</li>)}</ul> : null}
              </div>
            );
          })}
        </Sec>
      ) : null}
      {rem.length ? (
        <Sec title="Dette må du sette selv" aside={<span className="k-meta">{rem.length}</span>} testId="klargjor-remaining">
          <p className="k-meta mb-1">Ingen har gitt en pris på disse linjene. Skriv prisen her, eller åpne posten.</p>
          <ul>{rem.map((r) => <PriceLine key={`${r.item_id}-${r.element_id || "m"}`} r={r} ops={ops} onOpen={onOpenItem} />)}</ul>
        </Sec>
      ) : null}
      {ass.length || d.last ? (
        <Sec title="Antakelser som gjelder" aside={d.last ? <button type="button" onClick={() => hf.undo(d.last.id, ops)} disabled={Boolean(hf.working)} className="inline-flex items-center gap-1 text-[13px] font-medium text-[hsl(var(--navy))] hover:underline disabled:opacity-40" data-testid="klargjor-undo"><RotateCcw className="h-3.5 w-3.5" /> Angre alt ({ago(d.last.at)})</button> : null} testId="klargjor-assumptions">
          {ass.length ? (
            <ul>
              {ass.map((a) => (
                <li key={a.id} className="flex items-center gap-3 border-t border-black/[0.06] py-2.5 first:border-t-0" data-testid={`klargjor-assumption-${a.id}`}>
                  <button type="button" onClick={() => onOpenItem(a.kind === "linje" ? a.item_id : a.id)} className="min-w-0 flex-1 text-left" data-testid={`klargjor-assumption-open-${a.id}`}>
                    <span className="block text-[14.5px] leading-[1.4] text-foreground">{a.kind === "post" ? <><span className="font-mono text-[13px]">{a.ref}</span> · {a.title}</> : a.description}</span>
                    <span className="k-meta block">antatt {(a.antatt?.fields || []).join(", ")}{a.antatt?.by?.name ? ` · ${a.antatt.by.name.split(" ")[0]}` : ""}{a.antatt?.at ? ` · ${ago(a.antatt.at)}` : ""}</span>
                  </button>
                  <button type="button" onClick={() => hf.confirm(a.kind === "linje" ? { element_id: a.id } : { item_id: a.id }, ops)} disabled={Boolean(hf.working)} className="inline-flex h-8 shrink-0 items-center gap-1 rounded-full px-2.5 text-[13px] font-medium text-[hsl(var(--v-ok))] transition-colors hover:bg-[hsl(var(--v-ok-bg))] disabled:opacity-40" data-testid={`klargjor-confirm-${a.id}`}>{hf.working === `confirm-${a.id}` ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" strokeWidth={2.6} />} Bekreft</button>
                </li>
              ))}
            </ul>
          ) : <p className="k-meta">Alle antakelser er gjennomgått.</p>}
        </Sec>
      ) : null}
    </Inspector>
  );
}
